import React, { Fragment, ReactNode, useId } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import { DASHBOARD_CONTACT } from "../contact";
import Info from "../Info";
import {
  Band,
  Dimension,
  Domain,
  halfEnd,
  halfParts,
  nextHalf,
} from "./resilience-view";
import styles from "./resilience.module.scss";

export const K = "admin:companyDashboard.resilience";

export const VALIDATION_PAPER = "https://www.jmir.org/2026/1/e81714";

// Platform's `label` is English ("H1 2026"); each locale writes the half its
// own way, so the page builds it from the `value` instead.
export const useHalfLabel = () => {
  const { t } = useTranslation(["admin"]);
  return (value: string) => {
    const { year, number } = halfParts(value);
    return t(`${K}.half`, { year, number });
  };
};

export const useDomainName = () => {
  const { t } = useTranslation(["admin"]);
  return (domain: Domain) => t(`${K}.domains.${domain}`);
};

// Locations and business units show as the company stored them; the manager
// split is a yes or no.
export const useSegmentName = () => {
  const { t } = useTranslation(["admin"]);
  return (dimension: Dimension, name: string) => {
    if (dimension !== "manager") return name;
    if (name !== "true" && name !== "false") {
      throw new Error(`Not a manager segment: ${name}`);
    }
    return t(`${K}.cohorts.manager.${name}`);
  };
};

export const Section = ({
  title,
  info,
  eyebrow,
  children,
}: {
  title: string;
  info?: string;
  // A small uppercase label, or a legend, at the head's far edge.
  eyebrow?: ReactNode;
  children: ReactNode;
}) => {
  const headingId = useId();
  return (
    <section className={styles["card"]} aria-labelledby={headingId}>
      <div className={styles["card-head"]}>
        <h3 id={headingId} className={styles["card-title"]}>
          {title}
        </h3>
        {info && <Info figure={title} text={info} align="start" />}
        {eyebrow && <div className={styles["card-eyebrow"]}>{eyebrow}</div>}
      </div>
      {children}
    </section>
  );
};

const formatDay = (date: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);

// How current the page is: the shown half's last day, and the day the half
// after the latest published one is published.
export const useFreshness = () => {
  const { t, i18n } = useTranslation(["admin"]);
  const format = (date: Date) => formatDay(date, i18n.language);
  return (shown: string, latest: string) => {
    const through = halfParts(shown);
    const upcoming = halfParts(nextHalf(latest));
    const published = halfEnd(upcoming.year, upcoming.number);
    published.setUTCDate(published.getUTCDate() + 1);
    return t(`${K}.freshness`, {
      date: format(halfEnd(through.year, through.number)),
      next: format(published),
    });
  };
};

// A half's data runs to its last day: 30 June for H1, 31 December for H2.
export const useDataThrough = () => {
  const { t, i18n } = useTranslation(["admin"]);
  return (value: string) => {
    const { year, number } = halfParts(value);
    const end = halfEnd(year, number);
    return t(`${K}.dataThrough`, {
      date: formatDay(end, i18n.language),
    });
  };
};

export const NotEnoughData = ({ tone }: { tone?: "dark" }) => {
  const { t } = useTranslation(["admin"]);
  return (
    <p className={styles["not-enough"]} data-tone={tone}>
      {t(`${K}.notEnoughData`)}
    </p>
  );
};

// Shown when a company's groups give little or nothing to break its figures
// down by.
export const LimitedDataset = ({
  section,
  message,
  body,
}: {
  section: string;
  message?: string;
  // What sharing a roster would unlock here; the Resilience page's by default.
  body?: string;
}) => {
  const { t } = useTranslation(["admin"]);
  return (
    <div className={styles["limited"]} role="note">
      <span className={styles["limited-icon"]} aria-hidden="true">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
      </span>
      <div className={styles["limited-text"]}>
        <p className={styles["limited-eyebrow"]}>
          <span className={styles["limited-label"]}>
            {t(`${K}.limited.label`)}
          </span>{" "}
          <span className={styles["limited-section"]}>{section}</span>
        </p>
        {message && <p className={styles["limited-heading"]}>{message}</p>}
        <p className={styles["limited-body"]}>
          {body ?? t(`${K}.limited.body`)}
        </p>
      </div>
      <a
        className={styles["limited-cta"]}
        href={`mailto:${DASHBOARD_CONTACT}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t(`${K}.limited.cta`)}
      </a>
    </div>
  );
};

const BANDS: Band[] = ["steady", "watching", "nurturing"];

export const BandLegend = () => {
  const { t } = useTranslation(["admin"]);
  return (
    <ul className={styles["legend"]} aria-label={t(`${K}.legend.label`)}>
      {BANDS.map((one) => (
        <li key={one}>
          <BandLabel band={one} />{" "}
          <span className={styles["legend-range"]}>
            {t(`${K}.legend.${one}`)}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const BandLabel = ({ band }: { band: Band }) => {
  const { t } = useTranslation(["admin"]);
  return (
    <span className={styles["band"]} data-band={band}>
      {t(`${K}.bands.${band}`)}
    </span>
  );
};

const MARK = "\uE000";

// A translation with some of its values styled: each value in `nodes` is set
// where the translation places it, in whatever order the language puts them.
export const useRichText = () => {
  const { t } = useTranslation(["admin"]);
  return (
    key: string,
    options: Record<string, unknown>,
    nodes: Record<string, ReactNode>
  ) => {
    const marked = Object.fromEntries(
      Object.keys(nodes).map((name) => [name, `${MARK}${name}${MARK}`])
    );
    return t(key, { ...options, ...marked })
      .split(MARK)
      .map((part, index) => (
        <Fragment key={index}>{index % 2 === 1 ? nodes[part] : part}</Fragment>
      ));
  };
};
